import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, Chip, CircularProgress, Grid, Stack, Typography, Paper, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { fetchNewsById } from '../../controllers/newsController';
import { resolveNewsAssetUrl } from '../../api/httpClientNews';
import { resolveGalleryAssetUrl } from '../../api/httpClientGalleries';
import EntityGalleryPanel from '../../components/EntityGalleryPanel';
import { GALLERY_ENTITY_TYPE } from '../../constants/galleryEntityTypes';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { reorderNewsImages, uploadNewsImages as uploadNewsImagesAction, removeNewsImage as removeNewsImageAction } from '../../controllers/newsController';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ImageIcon from '@mui/icons-material/Image';
import BackButton from '../../components/BackButton';
import EditNewsDialog from '../../components/EditNewsDialog';

const hasHtml = (value) => {
  if (typeof value !== 'string') return false;
  return /<[^>]+>/.test(value);
};

const NewsDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const d = await fetchNewsById(id);
        if (!active) return;
        setData(d);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Haber alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const [images, setImages] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    setImages(Array.isArray(data?.images) ? data.images : []);
  }, [data]);

  const loadNews = async () => {
    try {
      const d = await fetchNewsById(id);
      setData(d);
      setImages(Array.isArray(d?.images) ? d.images : []);
    } catch (e) {
      setError(e?.message || 'Haber alınamadı');
    }
  };

  const hasAllGallery = Boolean(data?.galleryId || data?.GalleryId);

  const coverUrl = useMemo(() => {
    if (data?.coverImageUrl) {
      return resolveGalleryAssetUrl(data.coverImageUrl);
    }
    if (!hasAllGallery && images?.length) {
      return resolveNewsAssetUrl(images[0].url);
    }
    return '';
  }, [data?.coverImageUrl, hasAllGallery, images]);

  const SortableItem = ({ id, url }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: 'grab',
    };
    return (
      <Grid item ref={setNodeRef} style={style} {...attributes} {...listeners} xs={12}>
        <Paper variant="outlined" sx={{ width: 90, height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e6e6e6' }}>
          <Box component="img" src={resolveNewsAssetUrl(url)} alt={id} sx={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
        </Paper>
      </Grid>
    );
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = images.findIndex((i) => i.id === active.id);
    const newIndex = images.findIndex((i) => i.id === over.id);
    const newOrder = arrayMove(images, oldIndex, newIndex);
    setImages(newOrder);
    try {
      await reorderNewsImages({ newsId: data.id, imageIdsInOrder: newOrder.map((i) => i.id) });
    } catch (e) {
      // noop (istek başarısızsa görsel sıra eski haline getirilebilir)
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  const handleUploadFiles = async (fileList) => {
    const selectedFiles = Array.from(fileList || []).slice(0, 10);
    if (!selectedFiles.length) return;
    setUploadingImages(true);
    try {
      const added = await uploadNewsImagesAction({ newsId: data.id, files: selectedFiles });
      const normalized = Array.isArray(added) ? added : [];
      setImages((prev) => [
        ...prev,
        ...normalized.map((img) => ({
          id: img.id,
          url: img.url,
          order: img.order,
          createdAtUtc: img.createdAtUtc,
        })),
      ]);
    } catch (err) { }
    finally {
      setUploadingImages(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>;
  if (!data) return null;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <BackButton />
        <Button
          variant="contained"
          size="small"
          onClick={() => setEditOpen(true)}
          startIcon={<EditIcon sx={{ width: 16 }} />}
          sx={{
            textTransform: 'none',
            backgroundColor: '#E7EDF4',
            boxShadow: 'none',
            fontSize: 13,
            borderRadius: '5px',
            color: '#0E4D94',
            '&:hover': {
              backgroundColor: '#CFDBEA',
              boxShadow: 'none',
            },
          }}
        >
          Düzenle
        </Button>
      </Stack>
      <Card sx={{ boxShadow: 'none', backgroundColor: 'transparent' }}>
        <Stack spacing={3}>
          {/* Üst Kısım - Kapak Görseli ve İçerik Bilgileri */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '500px 1fr' }, gap: 3, alignItems: 'flex-start' }}>
            {/* Sol Taraf - Kapak Görseli ve Galeri */}
            <Box>
              <Stack spacing={2}>
                {/* Kapak Görseli */}
                <Box sx={{ position: 'relative', width: '100%', bgcolor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e6e6e6' }}>
                  {coverUrl ? (
                    <Box component="img" src={coverUrl} alt={data.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <ImageIcon sx={{ fontSize: 48, color: '#a3a3a3' }} />
                  )}
                </Box>

                {/* Galeri */}
                {hasAllGallery ? (
                  <EntityGalleryPanel
                    relatedEntityType={GALLERY_ENTITY_TYPE.News}
                    relatedEntityStringId={data.id}
                    initialGalleryId={data.galleryId || data.GalleryId}
                    embeddedGallery={data.gallery || data.Gallery}
                  />
                ) : (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '14px', color: '#444444', mb: 1.5 }}>Galeri</Typography>
                  {images?.length > 0 ? (
                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
                        <Grid container spacing={1} verticalAlign="middle">
                          {images.map((img, index) => (
                            <Grid key={img.id} item xs={12} sx={{ position: 'relative' }}>
                              <Box sx={{ position: 'relative' }}>
                                <SortableItem id={img.id} url={img.url} />
                                {index === 0 && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      left: 4,
                                      backgroundColor: '#DFFFEA',
                                      color: '#17C653',
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: '4px',
                                      fontSize: '10px',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Kapak
                                  </Box>
                                )}
                                <Tooltip title="Kaldır">
                                  <IconButton size="small" color="error" sx={{ position: 'absolute', top: 4, right: 4 }} onClick={(e) => { e.stopPropagation(); setSelectedImage(img); setConfirmOpen(true); }}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Grid>
                          ))}
                          {/* Görsel Ekle Kartı */}
                          <Grid item xs={12}>
                            <Box
                              component="label"
                              sx={{
                                width: 90,
                                height: 90,
                                border: '2px dashed #e6e6e6',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                backgroundColor: '#fafafa',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: '#0E4D94',
                                  backgroundColor: '#f5f5f5',
                                },
                                opacity: uploadingImages ? 0.6 : 1,
                                pointerEvents: uploadingImages ? 'none' : 'auto',
                              }}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                multiple
                                disabled={uploadingImages}
                                onChange={async (e) => {
                                  await handleUploadFiles(e.target.files);
                                  e.target.value = '';
                                }}
                              />
                              {uploadingImages ? (
                                <CircularProgress size={22} sx={{ color: '#0E4D94' }} />
                              ) : (
                                <>
                                  <AddPhotoAlternateIcon sx={{ fontSize: '24px', color: '#0E4D94' }} />
                                  <Typography variant="caption" sx={{ fontSize: '11px', color: '#0E4D94', fontWeight: 500 }}>
                                    Görsel Ekle
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <Stack spacing={2} alignItems="center">
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>Henüz görsel yok.</Typography>
                      <Box
                        component="label"
                        sx={{
                          width: 180,
                          height: 120,
                          border: '2px dashed #e6e6e6',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          backgroundColor: '#fafafa',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: '#0E4D94',
                            backgroundColor: '#f5f5f5',
                          },
                          opacity: uploadingImages ? 0.6 : 1,
                          pointerEvents: uploadingImages ? 'none' : 'auto',
                        }}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          multiple
                          disabled={uploadingImages}
                          onChange={async (e) => {
                            await handleUploadFiles(e.target.files);
                            e.target.value = '';
                          }}
                        />
                        {uploadingImages ? (
                          <CircularProgress size={24} sx={{ color: '#0E4D94' }} />
                        ) : (
                          <>
                            <AddPhotoAlternateIcon sx={{ fontSize: '28px', color: '#0E4D94' }} />
                            <Typography variant="caption" sx={{ fontSize: '12px', color: '#0E4D94', fontWeight: 500 }}>
                              Görsel Ekle
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Stack>
                  )}
                </Box>
                )}
              </Stack>
            </Box>

            {/* Sağ Taraf - İçerik Bilgileri */}
            <Box>
              <Stack spacing={2.5}>
                {/* Tarih ve Status */}
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                    {new Date(data.createdAtUtc).toLocaleString('tr-TR')}
                  </Typography>
                  {data.isActive ? (
                    <Chip size="small" label="Aktif" sx={{ backgroundColor: '#DFFFEA', color: '#17C653', fontWeight: 600, fontSize: '12px' }} />
                  ) : (
                    <Chip size="small" label="Pasif" sx={{ backgroundColor: '#FFEEF3', color: '#F8285A', fontWeight: 600, fontSize: '12px' }} />
                  )}
                </Stack>

                {/* Başlık */}
                <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '24px', color: '#444444' }}>{data.title}</Typography>

                {/* İçerik */}
                {data.content && (
                  <Box>
                    {hasHtml(data.content) ? (
                      <Box
                        sx={{ lineHeight: 1.7, fontSize: '14px', color: '#444444' }}
                        dangerouslySetInnerHTML={{ __html: data.content }}
                      />
                    ) : (
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: '14px', color: '#444444' }}>
                        {data.content}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Özet */}
                {data.summary && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, fontSize: '14px', color: '#444444' }}>
                      Özet
                    </Typography>
                    {hasHtml(data.summary) ? (
                      <Box
                        sx={{ fontSize: '14px', color: '#666666', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: data.summary }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666', lineHeight: 1.6 }}>
                        {data.summary}
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Card>

      <EditNewsDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        newsId={id}
        onSaved={loadNews}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Görseli Kaldır
          <IconButton
            onClick={() => setConfirmOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#666666' }}>
            {selectedImage ? 'Bu görseli kaldırmak istediğinize emin misiniz?' : 'Seçili görseli kaldırmak istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!selectedImage) return;
              try {
                await removeNewsImageAction({ newsId: data.id, imageId: selectedImage.id });
                setImages((prev) => prev.filter((i) => i.id !== selectedImage.id));
              } catch (e) { }
              finally {
                setConfirmOpen(false);
                setSelectedImage(null);
              }
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#E01E4A',
              },
            }}
          >
            Kaldır
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewsDetail;


